import { useCallback, useEffect, useRef, useState } from 'react';
import { Sticker, X } from 'lucide-react';
import type { FigurinhaItem } from '@/utils/figurinhas';

type FigurinhaPickerProps = {
    items: FigurinhaItem[];
    onPick: (code: string) => void;
    disabled?: boolean;
    /** Painel abre para cima (área de comentário em baixo) ou para baixo — só influencia scroll no mobile */
    placement?: 'above' | 'below';
};

const MD_MIN = '(min-width: 768px)';

function useIsDesktopMd() {
    const [isDesktop, setIsDesktop] = useState(() =>
        typeof window !== 'undefined' ? window.matchMedia(MD_MIN).matches : false,
    );
    useEffect(() => {
        const mq = window.matchMedia(MD_MIN);
        const onChange = () => setIsDesktop(mq.matches);
        onChange();
        mq.addEventListener('change', onChange);
        return () => mq.removeEventListener('change', onChange);
    }, []);
    return isDesktop;
}

export default function FigurinhaPicker({ items, onPick, disabled, placement = 'above' }: FigurinhaPickerProps) {
    const [open, setOpen] = useState(false);
    const rootRef = useRef<HTMLDivElement | null>(null);
    const keepTextareaFocusOnOpenRef = useRef(false);
    const isDesktop = useIsDesktopMd();

    const findNearestTextarea = useCallback((root: HTMLElement | null): HTMLTextAreaElement | null => {
        let container: HTMLElement | null = root?.parentElement ?? null;
        while (container) {
            const textarea = container.querySelector('textarea');
            if (textarea instanceof HTMLTextAreaElement) return textarea;
            container = container.parentElement;
        }
        return null;
    }, []);

    const handlePick = (code: string) => {
        onPick(code);
        setOpen(false);
    };

    const close = useCallback(() => setOpen(false), []);

    useEffect(() => {
        if (!open) return;
        const id = window.setTimeout(() => {
            const root = rootRef.current;
            if (!root) return;
            const textarea = findNearestTextarea(root);
            if (!textarea) {
                root.scrollIntoView({ behavior: 'smooth', block: 'center', inline: 'nearest' });
                return;
            }
            if (isDesktop) {
                if (keepTextareaFocusOnOpenRef.current) {
                    textarea.focus({ preventScroll: true });
                }
                return;
            }
            const viewportH = window.innerHeight || document.documentElement.clientHeight;
            const reserveBottom = Math.min(340, Math.round(viewportH * 0.45));
            const rect = textarea.getBoundingClientRect();
            const deltaDown = rect.bottom - (viewportH - reserveBottom);
            if (deltaDown > 0) {
                window.scrollBy({ top: deltaDown + 16, behavior: 'smooth' });
            } else {
                textarea.scrollIntoView({ behavior: 'smooth', block: 'center', inline: 'nearest' });
            }
            if (keepTextareaFocusOnOpenRef.current) {
                textarea.focus({ preventScroll: true });
            }
        }, 40);
        return () => window.clearTimeout(id);
    }, [open, isDesktop, findNearestTextarea]);

    useEffect(() => {
        if (!open) return;
        const onKey = (e: KeyboardEvent) => {
            if (e.key === 'Escape') close();
        };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, [open, close]);

    void placement;

    const grid = (
        <div className="overflow-y-auto overscroll-contain p-3 min-h-0 flex-1">
            {items.length === 0 ? (
                <p className="text-center text-white/45 text-sm py-8 px-2">Nenhuma figurinha disponível.</p>
            ) : (
                <div className="grid grid-cols-5 sm:grid-cols-6 md:grid-cols-7 gap-2">
                    {items.map((it) => (
                        <button
                            key={it.fig_codigo}
                            type="button"
                            title={it.fig_nome}
                            onClick={() => handlePick(it.fig_codigo)}
                            className="aspect-square rounded-xl bg-white/[0.04] hover:bg-white/[0.1] border border-transparent hover:border-amber-500/30 p-1.5 transition-colors flex items-center justify-center"
                        >
                            <img
                                src={it.fig_imagem}
                                alt=""
                                className="max-w-full max-h-full w-10 h-10 object-contain"
                                loading="lazy"
                            />
                        </button>
                    ))}
                </div>
            )}
        </div>
    );

    return (
        <div ref={rootRef} className="inline-flex">
            <button
                type="button"
                disabled={disabled}
                onClick={() => {
                    if (!open) {
                        const root = rootRef.current;
                        const textarea = findNearestTextarea(root);
                        keepTextareaFocusOnOpenRef.current =
                            !!textarea && document.activeElement === textarea;
                    } else {
                        keepTextareaFocusOnOpenRef.current = false;
                    }
                    setOpen((v) => !v);
                }}
                className="p-2 rounded-lg bg-white/5 border border-white/10 text-amber-300/90 hover:text-amber-200 hover:border-amber-500/40 transition-colors disabled:opacity-40"
                title="Figurinhas"
                aria-expanded={open}
                aria-haspopup="dialog"
            >
                <Sticker size={18} />
            </button>
            {open && (
                <>
                    <button
                        type="button"
                        className="fixed inset-0 z-[130] bg-black/70"
                        aria-label="Fechar figurinhas"
                        onClick={close}
                    />
                    {isDesktop ? (
                        <div
                            role="dialog"
                            aria-label="Figurinhas"
                            className="fixed inset-0 z-[131] flex items-center justify-center p-4 pointer-events-none"
                        >
                            <div
                                className="pointer-events-auto w-full max-w-lg max-h-[min(80vh,640px)] rounded-2xl border border-white/10 bg-neutral-900 shadow-2xl overflow-hidden flex flex-col"
                                onClick={(e) => e.stopPropagation()}
                            >
                                <div className="shrink-0 px-4 py-3 border-b border-white/10 bg-neutral-950/95 flex items-start justify-between gap-3">
                                    <div className="min-w-0">
                                        <p className="text-sm font-semibold text-white">Figurinhas</p>
                                        <p className="text-xs text-white/45 mt-0.5">Clique numa para inserir no texto</p>
                                    </div>
                                    <button
                                        type="button"
                                        onClick={close}
                                        className="shrink-0 p-2 rounded-lg text-white/60 hover:text-white hover:bg-white/10 transition-colors"
                                        aria-label="Fechar"
                                    >
                                        <X size={18} />
                                    </button>
                                </div>
                                {grid}
                            </div>
                        </div>
                    ) : (
                        <div
                            role="dialog"
                            aria-label="Figurinhas"
                            className="fixed inset-x-0 bottom-0 z-[131] max-h-[min(70vh,560px)] rounded-t-2xl border-t border-white/10 bg-neutral-900 shadow-2xl overflow-hidden flex flex-col"
                        >
                            <div className="shrink-0 px-3 pt-2 pb-2 border-b border-white/10 bg-neutral-950/95">
                                <div className="mx-auto mb-2 h-1 w-10 rounded-full bg-white/20" />
                                <p className="text-xs font-semibold text-white">Figurinhas</p>
                                <p className="text-[11px] text-white/45 mt-0.5">Toque para inserir no texto</p>
                            </div>
                            {grid}
                        </div>
                    )}
                </>
            )}
        </div>
    );
}
